// src/utils/imageOptimize.js
//
// Central place for REMOTE image optimization (Cloudinary, Unsplash, or any
// other CDN we add later). It works by rewriting the image URL with the
// CDN's own transformation params - no server-side work required, the CDN
// resizes/recompresses/reformats the image on request.
//
// For LOCAL bundled assets (src/assets/*), this file does nothing - see
// vite.config.js (vite-imagetools) and src/utils/localImage.js instead.

// Two standard breakpoints used everywhere in the app. Keep these in sync
// with the Tailwind `md` breakpoint (768px) that the rest of the UI uses.
export const BREAKPOINTS = {
  mobile: 640,   // served to phones / small tablets
  desktop: 1600, // served to laptops / desktops
};

const DEFAULT_QUALITY = {
  mobile: 65,
  desktop: 75,
};

/**
 * Rewrite a single remote image URL to request an optimized version from
 * its CDN. Unknown hosts are returned unchanged (safe no-op).
 */
export function optimizeImageUrl(url, { width = BREAKPOINTS.desktop, quality } = {}) {
  if (!url || typeof url !== "string") return url;

  const q = quality ?? (width <= BREAKPOINTS.mobile ? DEFAULT_QUALITY.mobile : DEFAULT_QUALITY.desktop);

  // Unsplash
  if (url.includes("images.unsplash.com")) {
    try {
      const u = new URL(url);
      u.searchParams.set("w", width);
      u.searchParams.set("auto", "format");
      u.searchParams.set("fit", "crop");
      u.searchParams.set("q", q);
      return u.toString();
    } catch {
      return url;
    }
  }

  // Cloudinary - insert transformation segment right after /upload/
  if (url.includes("res.cloudinary.com") && url.includes("/upload/")) {
    return url.replace("/upload/", `/upload/f_auto,q_${q},w_${width}/`);
  }

  // Add more CDNs here (imgix, Firebase-hosted images behind a proxy, etc.)

  return url;
}

/**
 * Given one remote URL, return a { mobile, desktop } pair of optimized URLs.
 * This is the single source of truth for "one version for mobile, one for
 * desktop" whenever the image comes from a CDN (Cloudinary/Unsplash).
 */
export function getResponsiveSources(
  url,
  { mobileWidth = BREAKPOINTS.mobile, desktopWidth = BREAKPOINTS.desktop } = {}
) {
  if (!url) return { mobile: url, desktop: url };
  return {
    mobile: optimizeImageUrl(url, { width: mobileWidth }),
    desktop: optimizeImageUrl(url, { width: desktopWidth }),
  };
}

// Candidate widths offered in a srcset. The browser - not us - picks the
// smallest one that still covers the image's actual rendered CSS size *
// device pixel ratio. This is what stops a 300px-wide gallery tile from
// downloading a 1600px-wide image just because that's the max width we
// support for hero banners.
const SRCSET_WIDTHS = [320, 480, 640, 800, 1200, 1600, 1920];

/**
 * Build a full `srcSet` string for a remote CDN image, e.g.
 *   "url?w=320 320w, url?w=480 480w, ..."
 * Pair this with a `sizes` attribute that describes how wide the image is
 * actually rendered (e.g. "(max-width: 767px) 100vw, 400px") so the browser
 * can pick the right candidate.
 */
export function getSrcSet(url, { maxWidth = BREAKPOINTS.desktop } = {}) {
  if (!url || typeof url !== "string") return "";
  return SRCSET_WIDTHS.filter((w) => w <= maxWidth)
    .map((w) => `${optimizeImageUrl(url, { width: w })} ${w}w`)
    .join(", ");
}
